import React, {useMemo} from 'react'
import type {ManualExamBookletLayoutSettings} from './core/_models'
import {normalizeManualExamLayoutSettings} from './core/_models'

const mmToPx = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) {
    return 0
  }
  return value * 1.25
}

const formatMultiline = (value?: string | null): React.ReactNode => {
  if (typeof value !== 'string') {
    return null
  }

  const lines = value
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)

  if (lines.length === 0) {
    return null
  }

  return lines.map((line, index) => (
    <span key={`manual-exam-preview-line-${index}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </span>
  ))
}

type ManualExamLayoutPreviewProps = {
  value: ManualExamBookletLayoutSettings
  className?: string
  title?: string
}

const ManualExamLayoutPreview: React.FC<ManualExamLayoutPreviewProps> = ({
  value,
  className,
  title,
}) => {
  const layout = useMemo(() => normalizeManualExamLayoutSettings(value), [value])
  const pageMargins = layout.pageMargins
  const coverSettings = layout.cover
  const backCover = layout.backCover
  const fontSizes = coverSettings.fontSizes
  const contentFontSizes = layout.contentFontSizes
  const shouldPrintFrontCover = layout.printFrontCover
  const shouldInsertBlankPage = layout.insertBlankPageAfterCover && shouldPrintFrontCover

  const coverTopOffset = Math.max(pageMargins.coverTop - pageMargins.top, 0)
  const paddingStyle: React.CSSProperties = {
    paddingTop: mmToPx(pageMargins.top),
    paddingRight: mmToPx(pageMargins.right),
    paddingBottom: mmToPx(pageMargins.bottom),
    paddingLeft: mmToPx(pageMargins.left),
    backgroundColor: '#fff',
    borderRadius: '0.75rem',
    border: '1px solid var(--bs-gray-300)',
    minHeight: 420,
    display: 'flex',
    flexDirection: 'column',
  }

  const cardClassName = [
    'manual-exam-layout-preview border rounded-3 bg-white p-4 shadow-sm',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const logoSrc = coverSettings.logoBase64?.trim() ?? ''
  const additionalNotesContent = formatMultiline(coverSettings.additionalNotes)
  const backCoverContent = formatMultiline(backCover.content)
  const fontSizeToStyle = (size: number): React.CSSProperties => ({fontSize: `${size}pt`})

  const toPixelValue = (value: number): string => `${Math.max(value, 0)}px`

  return (
    <div className={cardClassName}>
      {title && <h6 className='fw-semibold mb-3'>{title}</h6>}
      <div className='d-flex flex-wrap justify-content-center gap-2 mb-3'>
        <span className='badge bg-light text-dark border border-secondary'>
          Üst: {Math.round(pageMargins.top)} mm
        </span>
        <span className='badge bg-light text-dark border border-secondary'>
          Alt: {Math.round(pageMargins.bottom)} mm
        </span>
        <span className='badge bg-light text-dark border border-secondary'>
          Sol: {Math.round(pageMargins.left)} mm
        </span>
        <span className='badge bg-light text-dark border border-secondary'>
          Sağ: {Math.round(pageMargins.right)} mm
        </span>
        <span className='badge bg-secondary text-white border border-secondary'>
          Kapak Üst: {Math.round(pageMargins.coverTop)} mm
        </span>
      </div>
      {shouldPrintFrontCover ? (
        <div className='mx-auto' style={{maxWidth: 360}}>
          <div style={paddingStyle}>
            <div
              style={{marginTop: mmToPx(coverTopOffset)}}
              className='d-flex flex-column gap-2 align-items-stretch'
            >
              {coverSettings.showLogo && (
                <div className='text-center'>
                  {logoSrc ? (
                    <img
                      src={logoSrc}
                      alt='Kapak logo önizlemesi'
                      className='img-fluid border rounded-3 bg-light p-2'
                      style={{
                        maxHeight: toPixelValue(coverSettings.logoHeight),
                        maxWidth: toPixelValue(coverSettings.logoWidth),
                        width: toPixelValue(coverSettings.logoWidth),
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <div
                      className='d-inline-flex align-items-center justify-content-center border border-dashed rounded-3 bg-light text-uppercase fw-semibold text-muted'
                      style={{
                        width: toPixelValue(coverSettings.logoWidth),
                        height: toPixelValue(coverSettings.logoHeight),
                        fontSize: Math.max(Math.min(contentFontSizes.numbering, 18), 12),
                      }}
                    >
                      Logo
                    </div>
                  )}
                </div>
              )}
              {coverSettings.showInstitutionName && (
                <div
                  className='text-center text-uppercase fw-semibold text-muted'
                  style={fontSizeToStyle(fontSizes.institutionName)}
                >
                  Kurum Adı
                </div>
              )}
              {coverSettings.showFacultyName && (
                <div
                  className='text-center text-uppercase fw-semibold text-muted'
                  style={fontSizeToStyle(fontSizes.facultyName)}
                >
                  Fakülte Adı
                </div>
              )}
              {coverSettings.showDepartmentName && (
                <div
                  className='text-center text-uppercase fw-semibold text-muted'
                  style={fontSizeToStyle(fontSizes.departmentName)}
                >
                  Bölüm Adı
                </div>
              )}
              {coverSettings.showCourseName && (
                <div
                  className='text-center fw-semibold'
                  style={fontSizeToStyle(fontSizes.courseName)}
                >
                  Ders Adı
                </div>
              )}
              {coverSettings.showSemesterName && (
                <div
                  className='text-center text-muted'
                  style={fontSizeToStyle(fontSizes.semesterName)}
                >
                  2023-2024 Güz Dönemi
                </div>
              )}
              {coverSettings.showExamName && (
                <div
                  className='text-center fw-bold text-uppercase'
                  style={fontSizeToStyle(fontSizes.examName)}
                >
                  Sınav Adı
                </div>
              )}
              {(coverSettings.showBookletCode ||
                coverSettings.showExamDuration ||
                coverSettings.showQuestionCount) && (
                <div className='d-flex flex-wrap justify-content-center gap-2'>
                  {coverSettings.showBookletCode && (
                    <span
                      className='badge bg-info text-white'
                      style={fontSizeToStyle(fontSizes.metaInfo)}
                    >
                      {coverSettings.bookletCodeLabel}: A
                    </span>
                  )}
                  {coverSettings.showExamDuration && (
                    <span
                      className='badge bg-info text-white'
                      style={fontSizeToStyle(fontSizes.metaInfo)}
                    >
                      {coverSettings.durationLabel}: 60 dk
                    </span>
                  )}
                  {coverSettings.showQuestionCount && (
                    <span
                      className='badge bg-info text-white'
                      style={fontSizeToStyle(fontSizes.metaInfo)}
                    >
                      {coverSettings.questionCountLabel}: 50
                    </span>
                  )}
                </div>
              )}
              {coverSettings.showGlobalExamRules && (
                <div
                  className='border rounded-3 p-2 bg-light text-muted'
                  style={fontSizeToStyle(fontSizes.globalRules)}
                >
                  <div className='fw-semibold mb-1 text-center'>
                    {coverSettings.globalRulesHeader}
                  </div>
                  <div>Genel sınav kuralları örnek metni</div>
                </div>
              )}
              {coverSettings.showInstructions && (
                <div className='border rounded-3 p-2'>
                  <div
                    className='fw-semibold mb-1 text-center'
                    style={fontSizeToStyle(fontSizes.instructionsHeader)}
                  >
                    {coverSettings.instructionsHeader}
                  </div>
                  <div className='text-muted' style={fontSizeToStyle(fontSizes.instructionsBody)}>
                    Talimat örnek metni
                  </div>
                </div>
              )}
              {coverSettings.showAdditionalNotes && (
                <div
                  className='border border-secondary rounded-3 p-2 text-muted bg-light'
                  style={fontSizeToStyle(fontSizes.additionalNotes)}
                >
                  {additionalNotesContent ?? 'Ek notlar önizlemesi'}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className='alert alert-secondary mb-0'>Ön kapak basılmayacak.</div>
      )}
      {shouldInsertBlankPage && (
        <div className='alert alert-info mt-3 mb-0'>Kapaktan sonra bir boş sayfa eklenecek.</div>
      )}
      <div className='mt-4'>
        <h6 className='fw-semibold mb-2'>Soru Sayfası</h6>
        <div className='border rounded-3 bg-white p-3 shadow-sm'>
          <div className='d-flex align-items-start gap-2'>
            <span
              className='fw-semibold text-primary'
              style={{
                ...fontSizeToStyle(contentFontSizes.numbering),
                minWidth: '2.25rem',
              }}
            >
              1.
            </span>
            <p
              className='mb-0 text-dark flex-grow-1'
              style={fontSizeToStyle(contentFontSizes.questionText)}
            >
              Örnek soru metni bu alanda gösterilir. Yazı tipi boyutu ve satır yüksekliği seçilen
              değerlere göre güncellenir.
            </p>
          </div>
          <div className='mt-3 d-flex flex-column gap-2'>
            {['A', 'B', 'C', 'D'].map((letter) => (
              <div key={`preview-option-${letter}`} className='d-flex align-items-start gap-2'>
                <span
                  className='fw-semibold text-secondary'
                  style={{
                    ...fontSizeToStyle(contentFontSizes.numbering),
                    minWidth: '2.25rem',
                  }}
                >
                  {letter})
                </span>
                <span
                  className='text-muted flex-grow-1'
                  style={fontSizeToStyle(contentFontSizes.optionText)}
                >
                  Örnek seçenek metni bu alanda yer alır.
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {backCover.enabled && (
        <div className='mt-4'>
          <h6 className='fw-semibold mb-2'>Arka Kapak</h6>
          <div
            className='border rounded-3 p-3 bg-white text-muted'
            style={{minHeight: 100, ...fontSizeToStyle(backCover.fontSize)}}
          >
            {backCoverContent ?? 'Arka kapak içeriği önizlemesi'}
          </div>
        </div>
      )}
    </div>
  )
}

export default ManualExamLayoutPreview
